import { cn, useConfirm } from '@kalos/ui';

import { localStorageManager } from '../tools/storageManager';
import { flagColor } from './colors';
import { IS_PROD } from './evaluate';
import { type FlagReadState, useFlagReads } from './reads';
import { type FlagKey } from './registry';

const STATE_LABEL: Record<FlagReadState, string> = {
  on: 'on',
  off: 'off',
  // Some of what is on screen read the flag on and some off: an override changed between mounts.
  mixed: 'on and off',
};

/**
 * Flips the flag on this device through the local override (evaluate.ts) and reloads, since
 * every read is frozen at mount. Off clears the override, which outside prod reads as on.
 */
const flipLocally = (key: FlagKey, state: FlagReadState) => {
  const overrides = { ...(localStorageManager.get('kalos.flagOverrides') ?? {}) };
  if (state === 'off') delete overrides[key];
  else overrides[key] = false;
  localStorageManager.set('kalos.flagOverrides', overrides);
  window.location.reload();
};

/**
 * Floating under the header on the right of every screen outside prod: one pill per feature
 * flag that is rendering on this page, in that flag's colour, the same colour <Flagged> draws
 * around the UI it owns. Click a pill, confirm, and the page reloads with that flag off on this
 * device (and again for on). A flag that changes the whole screen frames the viewport instead
 * of a region. Nothing rendering, nothing shows; prod renders nothing.
 */
export const FeatureFlagLegend = () => {
  const reads = useFlagReads();
  const confirm = useConfirm();
  if (IS_PROD || reads.length === 0) return null;
  const framed = reads.filter((read) => read.page && read.state !== 'off');
  const flip = async (key: FlagKey, state: FlagReadState) => {
    const turningOn = state === 'off';
    // A reload drops anything unsaved on the screen, and the pill sits over phone content.
    const ok = await confirm({
      title: `Turn ${key} ${turningOn ? 'on' : 'off'} on this device?`,
      body: 'The page reloads to show it. Anything unsaved on this screen is lost.',
      actionButton: 'Reload',
    });
    if (ok) flipLocally(key, state);
  };
  return (
    <>
      {framed.map(({ key }, i) => (
        // Each frame is its own 3px band, one inside the previous, so every page-wide flag shows.
        <div
          key={key}
          aria-hidden
          className="pointer-events-none fixed z-[65]"
          style={{ inset: i * 3, boxShadow: `inset 0 0 0 3px ${flagColor(key)}` }}
        />
      ))}
      {/* DESIGN-SYSTEM-EXCEPTION: dev-only flag tooling needs one colour per flag · expires 2027-01-21 · Austin Kempa */}
      <ul
        aria-label="Feature flags on this page"
        className="fixed right-2 top-24 z-[70] flex max-w-[80vw] flex-col items-end gap-1"
      >
        {reads.map(({ key, state }) => (
          <li key={key}>
            <button
              type="button"
              aria-label={`Turn ${key} ${state === 'off' ? 'on' : 'off'} on this device`}
              className={cn(
                'touch:min-h-11 flex items-center gap-2 rounded-full border-2 bg-white/95 px-3 py-1 text-left text-xs font-semibold text-neutral-900 shadow',
                // Turned off here: greyed, so the eye goes to what is on.
                state === 'off' && 'text-neutral-500 opacity-70',
              )}
              style={{ borderColor: state === 'off' ? 'hsl(0 0% 72%)' : flagColor(key) }}
              onClick={() => void flip(key, state)}
            >
              <span
                aria-hidden
                className="size-2.5 shrink-0 rounded-full"
                style={{ background: state === 'off' ? 'transparent' : flagColor(key) }}
              />
              Feature flag: {key} · {STATE_LABEL[state]}
            </button>
          </li>
        ))}
      </ul>
    </>
  );
};
